/**
 * VisionShotlist gRPC adapter.
 */

import { createClient, Code, ConnectError } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { VisionShotlist } from '../gen/vision/shotlist/v1/shotlist_service_pb';
import { VisionAssets } from '../gen/vision/assets/v1/assets_service_pb';
import {
  castToLegacy,
  columnsToEnums,
  configToLegacy,
  presetBanksToLegacy,
  presetCategoryToEnum,
  presetToLegacy,
  propToLegacy,
  shotToLegacy,
} from './visionShotlistMapping.js';
import { config } from '../config';
import { getIdTokenForRequest } from './firebaseClient.js';

const BASE_URL = config.VISION_GRPC_BASE_URL;

export function isAlreadyExistsError(error) {
  return (
    error?.status === 409 ||
    (error instanceof ConnectError && error.code === Code.AlreadyExists)
  );
}

const authInterceptor = (next) => async (req) => {
  const token = await getIdTokenForRequest();
  if (token) {
    req.header.set('authorization', `Bearer ${token}`);
  }
  return next(req);
};

let cachedTransport = null;

function transport() {
  if (!cachedTransport) {
    cachedTransport = createGrpcWebTransport({
      baseUrl: BASE_URL,
      interceptors: [authInterceptor],
    });
  }
  return cachedTransport;
}

let cachedShotlist = null;
let cachedAssets = null;

function shotlistClient() {
  if (!cachedShotlist) {
    cachedShotlist = createClient(VisionShotlist, transport());
  }
  return cachedShotlist;
}

function assetsClient() {
  if (!cachedAssets) {
    cachedAssets = createClient(VisionAssets, transport());
  }
  return cachedAssets;
}

function snakeToCamel(field) {
  return field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function newShotToProto(shot) {
  return {
    shotNumber: shot.shot_number || '',
    scene: shot.scene || '',
    location: shot.location || '',
    description: shot.description || '',
    castIds: shot.cast_ids || [],
    frameSize: shot.frame_size || '',
    angle: shot.angle || '',
    cameraMovement: shot.camera_movement || '',
    equipment: shot.equipment || [],
    props: shot.props || [],
    directorNotes: shot.director_notes || '',
    dpNotes: shot.dp_notes || '',
    vfxNotes: shot.vfx_notes || '',
    sfxNotes: shot.sfx_notes || '',
    dialogue: shot.dialogue || '',
    shootingTime: shot.shooting_time || '',
  };
}

export async function getShotlist(projectId) {
  const response = await shotlistClient().getShotlist({ params: { projectId } });
  const result = response.result;

  return {
    shots: (result?.shots ?? []).map(shotToLegacy),
    cast: (result?.cast ?? []).map(castToLegacy),
    props: (result?.props ?? []).map(propToLegacy),
    config: configToLegacy(result?.config),
    presets: presetBanksToLegacy(result?.presets),
  };
}

export async function createShots(projectId, shots) {
  const response = await shotlistClient().createShots({
    params: { projectId, shots: shots.map(newShotToProto) },
  });

  return (response.result?.shots ?? []).map(shotToLegacy);
}

export async function updateShotField(projectId, shotId, field, value) {
  const response = await shotlistClient().updateShot({
    params: { projectId, shotId, [snakeToCamel(field)]: value },
    updateMask: { paths: [`params.${field}`] },
  });

  return response.result?.shot ? shotToLegacy(response.result.shot) : null;
}

export async function deleteShot(projectId, shotId) {
  await shotlistClient().deleteShot({ params: { projectId, shotId } });
}

export async function createCast(projectId, name) {
  const response = await shotlistClient().createCast({ params: { projectId, name } });
  return response.result?.cast ? castToLegacy(response.result.cast) : null;
}

export async function updateCast(projectId, castId, { name, color }) {
  const params = { projectId, castId };
  const paths = [];

  if (name !== undefined) {
    params.name = name;
    paths.push('params.name');
  }
  if (color !== undefined) {
    params.color = color;
    paths.push('params.color');
  }

  const response = await shotlistClient().updateCast({ params, updateMask: { paths } });
  return response.result?.cast ? castToLegacy(response.result.cast) : null;
}

export async function deleteCast(projectId, castId) {
  await shotlistClient().deleteCast({ params: { projectId, castId } });
}

export async function createProp(projectId, name) {
  const response = await shotlistClient().createProp({ params: { projectId, name } });
  return response.result?.prop ? propToLegacy(response.result.prop) : null;
}

export async function deleteProp(projectId, propId) {
  await shotlistClient().deleteProp({ params: { projectId, propId } });
}

export async function createShotlistPreset(projectId, category, value) {
  const response = await shotlistClient().createShotlistPreset({
    params: { projectId, category: presetCategoryToEnum(category), value },
  });

  return response.result?.preset
    ? presetToLegacy(response.result.category, response.result.preset)
    : null;
}

export async function updateShotlistConfig(projectId, visibleColumns, columnOrder) {
  await shotlistClient().updateShotlistConfig({
    params: {
      projectId,
      visibleColumns: columnsToEnums(visibleColumns),
      columnOrder: columnsToEnums(columnOrder),
    },
    updateMask: { paths: ['params.visible_columns', 'params.column_order'] },
  });
}

export async function setProjectImageFrameNumber(projectId, imageId, frameNumber, force = false) {
  const response = await assetsClient().setProjectImageFrameNumber({
    params: { projectId, imageId, frameNumber, force },
  });

  const outcome = response.result?.outcome;
  if (outcome?.case === 'conflict') {
    return { conflict: { existingImageId: outcome.value.existingImageId } };
  }
  return { applied: true };
}

export async function setProjectImageDescription(projectId, imageId, description) {
  await assetsClient().setProjectImageDescription({
    params: { projectId, imageId, description },
  });
}
